import Box from '@mui/material/Box'
import { ChangeEvent, FocusEventHandler, memo } from 'react'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const CheckboxButton = <T extends string>({
  value,
  checked,
  label,
  tooltip,
  onChange,
  onBlur,
  disabled,
  variant = 'default',
  border = true,
}: {
  value: T
  checked: boolean
  label: string
  tooltip?: string
  onChange?: (eventOrValue: ChangeEvent<HTMLInputElement>, checked: boolean) => void
  onBlur?: FocusEventHandler<HTMLElement>
  disabled?: boolean
  border?: boolean
  variant?: 'default' | 'compact'
}) => {
  const compact = variant === 'compact'
  return (
    <Box
      component="label"
      sx={theme => ({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        border: border ? `1px solid ${theme.palette.primary.main}` : 'none',
        borderRadius: 1,
        cursor: 'pointer',
        position: 'relative',
        paddingRight: compact ? 0.75 : 1,
        minHeight: compact ? 30 : undefined,
        backgroundColor: '#fff',
      })}
    >
      <Box>
        <Checkbox
          size="small"
          value={value}
          checked={!!checked}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          sx={{
            padding: compact ? 0.5 : undefined,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          component={'span'}
          variant="subtitle2"
          sx={{
            fontSize: compact ? 11 : 12,
            lineHeight: compact ? '11px' : '12px',
          }}
        >
          {label}
        </Typography>
      </Box>
      {tooltip && (
        <Box
          sx={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
          }}
        >
          <Tooltip title={tooltip} enterTouchDelay={0} placement="top">
            <InfoOutlinedIcon
              color="primary"
              sx={{ fontSize: compact ? 14 : 16, backgroundColor: 'white' }}
            />
          </Tooltip>
        </Box>
      )}
    </Box>
  )
}

export default memo(CheckboxButton)
